import React, { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils.js";
import { shapeClass, type Shape } from "../../lib/shape.js";

/* ---------------- Skeleton ---------------- */

/** The corner words a Skeleton takes, read from `lib/shape.ts` (C7). */
export type SkeletonShape = Extract<
  Shape,
  "rounded" | "pill" | "circle" | "panel" | "card"
>;

/* Shape comes first and the shimmer classes after it, so the default
   (`rounded`) renders today's exact class string. */
const skeletonShape = cva("", {
  variants: {
    shape: {
      rounded: shapeClass("rounded"),
      pill: shapeClass("pill"),
      /* Square box from one side; the caller sets a width or a height. */
      circle: `${shapeClass("circle")} aspect-square`,
      panel: shapeClass("panel"),
      card: shapeClass("card"),
    },
  },
  defaultVariants: { shape: "rounded" },
});

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Corner of the placeholder. `rounded` (default, 8px) · `pill` fully round
   *  ends on the caller's width · `circle` fully round and `aspect-square`
   *  (give it a width or a height) · `panel` 10px, for placeholders that use
   *  that corner (the base radius, Card's inset corner) · `card` 14px, for a
   *  placeholder that stands in for a card. */
  shape?: SkeletonShape | undefined;
}

/**
 * A loading placeholder: a filled block with the `pb-shimmer` sweep. Every
 * blinking placeholder uses Skeleton; do not hand-build one with
 * `animate-pulse`.
 *
 * Skeleton has no label. The region's loading name comes from
 * `AsyncSection status="loading" loadingLabel` (it renders `role="status"`,
 * `aria-busy` and "Loading {loadingLabel}" around the skeleton slot) or from
 * the region's own `role="status"` wrapper. Keep that name when a
 * placeholder adopts Skeleton.
 *
 * `shape="panel"` is for 10px placeholders and `shape="card"` for
 * card-shaped placeholders; use them instead of a `rounded-lg` or
 * `rounded-xl` className.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  function Skeleton({ shape, className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        /* `pb-shimmer` sweeps a highlight over the fill (ADR-0092) instead of
           blinking the whole block. `motion-reduce:animate-none` drops the
           keyframes (not just shortens them like the global theme.css guard)
           under prefers-reduced-motion, so the skeleton renders a static
           frame — required for the deterministic VR gate, which freezes the
           clock and would otherwise pin the sweep at a random phase. Same
           intent as the recharts `isAnimationActive` gating. */
        className={cn(
          skeletonShape({ shape }),
          "bg-pb-card-elevated pb-shimmer motion-reduce:animate-none",
          className,
        )}
        {...rest}
      />
    );
  },
);
